import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

import { ReactComponent as PlusSvg } from "../../../../assets/icons/plus-square.svg";
import {
  TRUE,
  messages,
  EMPTY_STRING,
  numberConstants,
  localStorageKeys,
  eventBookingStatus,
  defaultEventSetupObject,
} from "../../../../constants";
import { getLocalStorageItem } from "../../../../system";
import {
  deepClone,
  customToast,
  filterByStatus,
  filterItemsById,
  generateUniqueId,
  isDateBeforeMinimumBookingDate,
  updateFormStateOnDelete,
} from "../../../../shared";
import InfoModal from "../../../../shared/components/Modal/InfoModal";
import CustomModal from "../../../../shared/components/Modal/CustomModal";
import ConfirmationModal from "../../../../shared/components/Modal/ConfirmationModal";
import AnimationWrapper from "../../../../shared/components/Animation/AnimationWrapper";
import InputGroupWrapper from "../../../../shared/components/InputGroup/InputGroupWrapper";
import { eventBookingActions } from "../../../../redux/actions";
import EventSetup from "./EventSetup";

const EventSetupForm = (props) => {
  const { t } = useTranslation("common");

  const focusErrorFieldRef = useRef([]);
  const [showModal, setShowModal] = useState(false);
  const [showEventCollectionModal, setShowEventCollectionModal] =
    useState(false);
  const [isShowInfoModal, setIsShowInfoModal] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [deleteEventModalMsg, setDeleteEventModalMsg] = useState(EMPTY_STRING);
  const [emptyStates, setEmptyStates] = useState(
    Array.isArray(props.formState)
      ? props.formState.reduce((acc, event) => {
          if (event.status !== eventBookingStatus.CANCEL) {
            acc[event.id] = {
              eventDate: false,
              eventStartTime: false,
              sbStartTime: false,
              eventDateInvalid: false,
            };
          }
          return acc;
        }, {})
      : {}
  );

  const handleAddEvent = () => {
    if (props.totalEvents >= 10) {
      customToast(
        messages.YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_EVENTS_FOR_YOUR_ORDER,
        toast.TYPE.ERROR
      );
      return;
    }

    const newEvent = {
      ...deepClone(defaultEventSetupObject),
      id: generateUniqueId(),
    };
    props.setTotalEvents(props.totalEvents + 1);
    props.setFormState([...props.formState, newEvent]);
  };

  const handleSubmit = (event, values, errors) => {
    let isAnyInvalid = false;
    const updatedFormState = [];
    const updatedEmptyStates = [];
    const seenEventNames = new Set();
    let duplicateEventName = null;
    props.formState.forEach((eventItem) => {
      if (eventItem.status === eventBookingStatus.CANCEL) {
        updatedFormState.push(eventItem);
        return;
      }

      const isDateMissing = !eventItem.eventDate;
      const isEventStartTimeMissing = !eventItem.eventStartTime;
      const isShutterBStartTimeMissing = !eventItem.sbStartTime;
      const isLatLngMissing =
        eventItem.eventLocation.latitude == null ||
        eventItem.eventLocation.longitude == null;

      const eventName = eventItem.eventName?.trim().toLowerCase();

      if (eventName) {
        if (seenEventNames.has(eventName)) {
          duplicateEventName = eventItem.eventName;
          return;
        }
        seenEventNames.add(eventName);
      }

      const isDateInvalid = eventItem.eventDate
        ? isDateBeforeMinimumBookingDate(new Date(eventItem.eventDate))
        : false;

      // Create and store validation error flags for this index
      const errorFlags = {
        eventDate: isDateMissing,
        eventStartTime: isEventStartTimeMissing,
        sbStartTime: isShutterBStartTimeMissing,
        eventDateInvalid: !isDateMissing && isDateInvalid,
      };

      updatedEmptyStates[eventItem.id] = errorFlags;

      const hasMissingField =
        isDateMissing ||
        isDateInvalid ||
        isEventStartTimeMissing ||
        isShutterBStartTimeMissing ||
        isLatLngMissing;

      if (hasMissingField) {
        if (!isAnyInvalid) {
          setEmptyStates((prev) => ({
            ...prev,
            [eventItem.id]: errorFlags,
          }));

          if (isLatLngMissing) {
            customToast(
              "Please select location from dropdown",
              toast.TYPE.ERROR
            );
          }
        }
        isAnyInvalid = true;
        return; // skip this entry in the submission array
      }
      // If valid, collect the data
      updatedFormState.push({
        ...eventItem,
        id: eventItem.id,
        eventName: eventItem.eventName,
        eventDate: eventItem.eventDate,
        eventStartTime: eventItem.eventStartTime,
        eventLocation: {
          ...eventItem.eventLocation,
          currentLocation:
            eventItem?.eventLocation?.currentLocation || EMPTY_STRING,
          latitude: eventItem?.eventLocation?.latitude,
          longitude: eventItem?.eventLocation?.longitude,
          postalCode: eventItem?.eventLocation?.postalCode || EMPTY_STRING,
        },
        apartment: eventItem.apartment,
        eventType: eventItem.eventType,
        additionalNotes: eventItem.additionalNotes,
        notes: eventItem.additionalNotes,
        sbStartTime: eventItem.sbStartTime,
        duration: eventItem.duration,
        termsAndConditions: eventItem.termsAndConditions,
        privacyPolicy: eventItem.privacyPolicy,
        contactName: eventItem.contactName,
        phoneNumber: eventItem.phoneNumber,
      });
    });

    if (isAnyInvalid) {
      setEmptyStates(updatedEmptyStates); // apply all error states
      return; // prevent submission
    }

    if (duplicateEventName) {
      customToast(
        `Duplicate event name "${duplicateEventName}"`,
        toast.TYPE.ERROR
      );
      return;
    }

    // Reset errors if everything is valid
    setEmptyStates({});
    props.setFormState(updatedFormState);

    const isSingleEvent =
      updatedFormState.filter((f) => f.status !== eventBookingStatus.CANCEL)
        .length === numberConstants.ONE;

    if (isSingleEvent) {
      props.onNext();
    } else {
      setShowEventCollectionModal(true);
    }
  };

  const handleInvalidSubmit = (event, errors, values) => {
    if (errors.length > 0) {
      for (const errorFieldName of errors) {
        const match = errorFieldName.match(/event\[(\d+)\]/);
        if (match) {
          const errorIndex = parseInt(match[1], 10);
          const eventItem = props.formState[errorIndex];
          if (!eventItem) continue;

          const currentRefs = focusErrorFieldRef.current[eventItem.id] || [];

          for (let toFocusElm of currentRefs) {
            if (toFocusElm.htmlFor === errorFieldName.split(".").pop()) {
              toFocusElm.focus();
            }
          }
        }
      }
    }

    // Loop through all event entries
    for (let i = 0; i < props.formState.length; i++) {
      const eventItem = props.formState[i];
      const isDateMissing = !eventItem.eventDate;
      const isEventStartTimeMissing = !eventItem.eventStartTime;
      const isShutterBStartTimeMissing = !eventItem.sbStartTime;

      const isDateInvalid = eventItem.eventDate
        ? isDateBeforeMinimumBookingDate(new Date(eventItem.eventDate))
        : false;

      if (
        isDateMissing ||
        isEventStartTimeMissing ||
        isShutterBStartTimeMissing
      ) {
        setEmptyStates((prev) => ({
          ...prev,
          [eventItem.id]: {
            eventDate: isDateMissing,
            eventDateInvalid: !isDateMissing && isDateInvalid,
            eventStartTime: isEventStartTimeMissing,
            sbStartTime: isShutterBStartTimeMissing,
          },
        }));
      }
    }
  };

  const updateEmptyState = (eventId, newState) => {
    setEmptyStates((prev) => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        ...newState,
      },
    }));
  };

  const handleDeleteEvent = (event) => {
    setDeleteEventId(event?.id);

    const index = props.formState
      .filter((e) => e.status !== eventBookingStatus.CANCEL)
      .findIndex((e) => e.id === event?.id);

    if (event?.status === eventBookingStatus.EDITED) {
      setDeleteEventModalMsg(
        `${messages.EVENT_DELETION_WILL_TRIGGER_REFUND_IF_PROCEEDED} ${
          index + 1
        }`
      );
    } else {
      setDeleteEventModalMsg(
        `${messages.ARE_YOU_SURE_YOU_WANT_TO_DELETE_EVENT} ${index + 1}`
      );
    }
    setShowModal(true);
  };

  const confirmDeleteEvent = (eventId) => {
    const event = props.formState.find((e) => e.id === eventId);
    const shouldUpdateStatusToDeleted =
      props.isEditEvent &&
      (event?.status === eventBookingStatus.EDITED ||
        event?.status === eventBookingStatus.NOTIFY_ADMIN);

    setEmptyStates((prev) =>
      shouldUpdateStatusToDeleted
        ? { ...prev, [eventId]: {} }
        : Object.fromEntries(
            Object.entries(prev).filter(([id]) => id !== String(eventId))
          )
    );

    const updatedModel = updateFormStateOnDelete(
      props.formState,
      eventId,
      props.isEditEvent,
      props.editEvents
    );
    props.setFormState(updatedModel);

    if (!shouldUpdateStatusToDeleted) {
      props.setTotalEvents(props.totalEvents - 1);
    }

    const storedRecaps = getLocalStorageItem(localStorageKeys.ADD_RECAPZ, []);
    const updatedRecaps = filterItemsById(
      storedRecaps,
      filterByStatus(updatedModel ?? [], [eventBookingStatus.CANCEL])
    );

    props.setAddRecapz(updatedRecaps);

    if (props.selectedEventId === eventId) {
      const visibleEvents = filterByStatus(updatedModel, [
        eventBookingStatus.CANCEL,
      ]);
      if (visibleEvents.length > 0) {
        props.setSelectedEventId(visibleEvents[0].id);
        props.setEventName(visibleEvents[0].eventName);
      } else {
        props.setSelectedEventId(null);
        props.setEventName(EMPTY_STRING);
      }
    }
  };

  const handleConfirm = () => {
    if (deleteEventId !== null) {
      confirmDeleteEvent(deleteEventId);
      setDeleteEventId(null);
      setDeleteEventModalMsg(EMPTY_STRING);
      setShowModal(false);
    }
  };

  const handleInfoModal = () => {
    setIsShowInfoModal(true);
  };

  const updateEventField = (value) => {
    const updatedState = props.formState.map((event) => ({
      ...event,
      eventGroupName: value,
    }));
    props.setFormState(updatedState);
  };

  return (
    <div>
      <div className="event-setup-container">
        <AvForm
          key={props.avFormState}
          id="event-setup-form"
          onValidSubmit={handleSubmit}
          onInvalidSubmit={handleInvalidSubmit}
          noValidate
          autoComplete="off"
        >
          {Array.isArray(props.formState) &&
            filterByStatus(props.formState, [eventBookingStatus.CANCEL]).map(
              (event, index) => (
                <AnimationWrapper animationKey={event.id}>
                  <EventSetup
                    key={event.id}
                    index={index}
                    event={event}
                    emptyStates={emptyStates[event.id]}
                    setEmptyStates={(newState) =>
                      updateEmptyState(event.id, newState)
                    }
                    focusErrorFieldRef={focusErrorFieldRef}
                    handleDeleteEvent={handleDeleteEvent}
                  />
                </AnimationWrapper>
              )
            )}
          {props.hasMultiEvents !== TRUE && (
            <div className="add-new-event mt-4 d-inline-flex">
              <button type="button" onClick={handleAddEvent}>
                <span className="add-event-icon">
                  <PlusSvg />
                </span>
                <span className="add-event-title">
                  {t("add_another_event")}
                </span>
              </button>
              {Array.isArray(props.formState) &&
                props.formState.length === 1 && (
                  <button type="button" onClick={handleInfoModal}>
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="ml-2 info-icon"
                    />
                  </button>
                )}
            </div>
          )}
          <div className="d-flex justify-content-center sticky-button-row pt-4 mt-4">
            <button type="submit" className="button-common button-fill">
              {t("continue")}
            </button>
          </div>
        </AvForm>
      </div>

      <ConfirmationModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        dialogText={`${deleteEventModalMsg}`}
        maxWidth="xs"
      />

      <InfoModal
        maxWidth="md"
        open={isShowInfoModal}
        onClose={() => setIsShowInfoModal(false)}
        dialogText={
          <div className="event-info-modal">
            <p className="mb-2">
              you can now book coverage for multiple events or event days — all
              in one flow.
            </p>
            <ul>
              <li>
                if your event runs across multiple days (like a trade show or
                conference), book each date separately — or use this feature to
                split coverage into different shifts within the same event.
              </li>
              <li>
                ⁠you can also line up all your upcoming events for the year —
                one and done.
              </li>
            </ul>
          </div>
        }
      />

      <CustomModal
        show={showEventCollectionModal}
        onClose={() => setShowEventCollectionModal(false)}
        title="name your event collection"
      >
        <div className="event-collection-modal-content">
          <div className="row">
            <div className="col-12">
              <div className="heading">
                give your group of events a name, this will be your main folder
                that groups all events together.
                <br />
                <span>
                  example: tech expo winter 2026 or spring events 2026.
                </span>
              </div>
            </div>
          </div>

          <AvForm
            key={props.avFormState}
            id="event-collection-form"
            onValidSubmit={() => {
              setShowEventCollectionModal(false);
              props.onNext();
            }}
            noValidate
            autoComplete="off"
          >
            <div className="row mt-4 mb-5">
              <div className="col-12">
                <label
                  className="form-label label-content-500"
                  htmlFor="eventGroupName"
                >
                  name your event collection
                </label>
                <InputGroupWrapper>
                  <AvField
                    name="eventGroupName"
                    type="text"
                    className="form-control pr-4"
                    value={props.formState[0]?.eventGroupName || EMPTY_STRING}
                    placeHolder="e.g. tech expo winter 2026"
                    maxLength="150"
                    validate={{
                      required: {
                        value: true,
                        errorMessage: "Please enter name your event collection",
                      },
                    }}
                    onChange={(event, value) => updateEventField(value)}
                  />
                </InputGroupWrapper>
              </div>
            </div>
            <div className="d-flex justify-content-center py-3 my-3">
              <button type="submit" className="button-common button-fill">
                {t("continue")}
              </button>
            </div>
          </AvForm>
        </div>
      </CustomModal>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  formState: state.eventBooking.formState,
  editEvents: state.eventBooking.editEvents,
  isEditEvent: state.eventBooking.isEditEvent,
  totalEvents: state.eventBooking.totalEvents,
  selectedEventId: state.eventBooking.selectedEventId,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setEventName: (data) => dispatch(eventBookingActions.setEventName(data)),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  setEventTypes: (data) => dispatch(eventBookingActions.setEventTypes(data)),
  setTotalEvents: (data) => dispatch(eventBookingActions.setTotalEvents(data)),
  setSelectedEventId: (data) =>
    dispatch(eventBookingActions.setSelectedEventId(data)),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(EventSetupForm));
